import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  FiBriefcase,
  FiFileText,
  FiExternalLink,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiEdit2,
  FiMail,
  FiPhone,
  FiMapPin,
  FiGlobe,
  FiPackage,
  FiSpeaker,
  FiFolder,
  FiTag,
  FiSearch,
  FiX,
  FiChevronDown,
  FiImage,
  FiAlertTriangle,
} from "react-icons/fi";
import axiosInstance from "../config/axiosInstance";
import EmptyState from "../components/EmptyState";
import NoData from "../components/NoData";
import BreadcrumbTrail from "../components/BreadcrumbTrail";
import ResponsiveModal from "../components/ResponsiveModal";
import { useAuth } from "../contexts/AuthContext";
import {
  audienceLabel,
  formatDate,
  statusLabel,
  statusTone,
  timeAgo,
} from "../utils/format";

const TABS = [
  { id: "overview", label: "Visão geral" },
  { id: "segmentation", label: "Segmentação" },
  { id: "products", label: "Produtos" },
  { id: "ads", label: "Anúncios" },
  { id: "documents", label: "Documentos" },
];

const REVIEW_OPTIONS = [
  { status: "APROVADO", label: "Aprovar", color: "#166534", icon: FiCheckCircle },
  { status: "REJEITADO", label: "Reprovar", color: "#b91c1c", icon: FiXCircle },
  { status: "PENDENTE", label: "Voltar para análise", color: "#92400e", icon: FiClock },
];

const STATUS_ICON = {
  APROVADO: FiCheckCircle,
  PENDENTE: FiClock,
  REJEITADO: FiXCircle,
};

const COMPANY_TYPE_LABEL = {
  INDUSTRIA: "Indústria",
  FORNECEDOR_PRODUTOR: "Fornecedor / Produtor",
  DISTRIBUIDOR: "Distribuidor",
};

const statusText = (status) =>
  status === "REJEITADO" ? "Reprovado" : statusLabel(status);

const initialsOf = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const StatusPill = ({ status, size = "sm" }) => {
  if (!status) return <span className="muted">-</span>;
  const Icon = STATUS_ICON[status];

  return (
    <span
      className="badge"
      data-tone={statusTone(status)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        whiteSpace: "nowrap",
        ...(size === "lg" ? { fontSize: "0.82rem", padding: "6px 12px" } : {}),
      }}
    >
      {Icon && <Icon size={size === "lg" ? 14 : 12} />}
      {statusText(status)}
    </span>
  );
};

const Row = ({ label, children }) => (
  <dl className="cdet-row">
    <dt>{label}</dt>
    <dd>{children || "-"}</dd>
  </dl>
);

const PaginationControls = ({ currentPage, totalPages, onChangePage }) => {
  if (!totalPages || totalPages <= 1) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "0.75rem",
        marginTop: "1rem",
      }}
    >
      <button
        type="button"
        className="button button--ghost"
        onClick={() => onChangePage(Math.max(1, currentPage - 1))}
        disabled={currentPage <= 1}
      >
        Anterior
      </button>
      <span className="muted">
        Página {currentPage} de {totalPages}
      </span>
      <button
        type="button"
        className="button button--ghost"
        onClick={() => onChangePage(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage >= totalPages}
      >
        Próxima
      </button>
    </div>
  );
};

const CompanyDetailsPage = () => {
  const { companyId } = useParams();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  const [products, setProducts] = useState([]);
  const [productsMeta, setProductsMeta] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    itemsPerPage: 10,
  });
  const [ads, setAds] = useState([]);
  const [adsMeta, setAdsMeta] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    itemsPerPage: 10,
  });
  const [tabCounts, setTabCounts] = useState({
    overview: 0,
    segmentation: 0,
    products: 0,
    ads: 0,
    documents: 0,
  });
  const [documents, setDocuments] = useState([]);
  const [companyCategories, setCompanyCategories] = useState([]);
  const [companyRegions, setCompanyRegions] = useState([]);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  const [companyFormError, setCompanyFormError] = useState("");
  const [companyForm, setCompanyForm] = useState({
    nomeFantasia: "",
    razaoSocial: "",
    cnpj: "",
    statusVerificacao: "PENDENTE",
    emailCorporativo: "",
    whatsapp: "",
    siteOficial: "",
    enderecoCidade: "",
    enderecoEstado: "",
  });
  const [verificationSaving, setVerificationSaving] = useState(false);
  const [verificationError, setVerificationError] = useState("");

  const [productsPage, setProductsPage] = useState(1);
  const [productsItemsPerPage, setProductsItemsPerPage] = useState(10);
  const [productsQuery, setProductsQuery] = useState("");
  const [productsUrgency, setProductsUrgency] = useState("");
  const [productsStatus, setProductsStatus] = useState("");
  const [productsRefreshKey, setProductsRefreshKey] = useState(0);
  const [productStatusSavingId, setProductStatusSavingId] = useState(null);
  const [productStatusError, setProductStatusError] = useState("");
  const [openProductMenuId, setOpenProductMenuId] = useState(null);
  const [rejectingProduct, setRejectingProduct] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [previewProduct, setPreviewProduct] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const productMenuRef = useRef(null);

  const [adsPage, setAdsPage] = useState(1);
  const [adsItemsPerPage, setAdsItemsPerPage] = useState(10);
  const [adsQuery, setAdsQuery] = useState("");
  const [adsStatus, setAdsStatus] = useState("");
  const [adsAudience, setAdsAudience] = useState("");

  const canAccessCompanies = ["POWER_USER", "ADMIN"].includes(user?.role);

  useEffect(() => {
    const fetchData = async () => {
      if (!canAccessCompanies || !companyId) return;

      setLoading(true);
      try {
        const response = await axiosInstance.get(`/companies/${companyId}/details`, {
          params: {
            productsPage,
            productsItemsPerPage,
            productsQuery,
            productsUrgency,
            productsStatus,
            adsPage,
            adsItemsPerPage,
            adsQuery,
            adsStatus,
            adsAudience,
          },
        });
        const details = response?.data?.data;

        setCompany(details?.company || null);
        setProducts(details?.products?.data || []);
        setProductsMeta({
          currentPage: details?.products?.currentPage || 1,
          totalPages: details?.products?.totalPages || 1,
          totalCount: details?.products?.totalCount || 0,
          itemsPerPage: details?.products?.itemsPerPage || productsItemsPerPage,
        });
        setAds(details?.ads?.data || []);
        setAdsMeta({
          currentPage: details?.ads?.currentPage || 1,
          totalPages: details?.ads?.totalPages || 1,
          totalCount: details?.ads?.totalCount || 0,
          itemsPerPage: details?.ads?.itemsPerPage || adsItemsPerPage,
        });
        setDocuments(details?.documents || []);
        setCompanyCategories(details?.categories || []);
        setCompanyRegions(details?.regions || []);
        setTabCounts(
          details?.tabCounts || {
            overview: details?.company ? 1 : 0,
            segmentation:
              (details?.totals?.categories || 0) + (details?.totals?.regions || 0),
            products: details?.totals?.products || 0,
            ads: details?.totals?.ads || 0,
            documents: details?.totals?.documents || 0,
          }
        );
      } catch (error) {
        setCompany(null);
        setProducts([]);
        setProductsMeta({
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          itemsPerPage: productsItemsPerPage,
        });
        setAds([]);
        setAdsMeta({
          currentPage: 1,
          totalPages: 1,
          totalCount: 0,
          itemsPerPage: adsItemsPerPage,
        });
        setDocuments([]);
        setCompanyCategories([]);
        setCompanyRegions([]);
        setTabCounts({ overview: 0, segmentation: 0, products: 0, ads: 0, documents: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    canAccessCompanies,
    companyId,
    productsPage,
    productsItemsPerPage,
    productsQuery,
    productsUrgency,
    productsStatus,
    productsRefreshKey,
    adsPage,
    adsItemsPerPage,
    adsQuery,
    adsStatus,
    adsAudience,
  ]);

  useEffect(() => {
    if (!openProductMenuId) return undefined;

    const handleClickOutside = (event) => {
      if (productMenuRef.current && !productMenuRef.current.contains(event.target)) {
        setOpenProductMenuId(null);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setOpenProductMenuId(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [openProductMenuId]);

  const selectedCategoryLabels = useMemo(
    () =>
      companyCategories
        .map((item) => item?.Category?.nome || item?.category?.nome)
        .filter(Boolean),
    [companyCategories]
  );

  const selectedRegionLabels = useMemo(
    () =>
      companyRegions
        .map((item) => {
          const region = item?.Region || item?.region;
          if (!region) return null;
          return `${region.stateCode}${region.city ? ` - ${region.city}` : ""}`;
        })
        .filter(Boolean),
    [companyRegions]
  );

  const displayName =
    company?.nomeFantasia || company?.razaoSocial || "Detalhes do cliente";

  const breadcrumbItems = useMemo(
    () => [{ label: "Empresas", to: "/empresas" }, { label: displayName }],
    [displayName]
  );

  const openEditModal = () => {
    setCompanyFormError("");
    setCompanyForm({
      nomeFantasia: company?.nomeFantasia || company?.fantasyName || "",
      razaoSocial: company?.razaoSocial || company?.name || "",
      cnpj: company?.cnpj || "",
      statusVerificacao: company?.statusVerificacao || "PENDENTE",
      emailCorporativo: company?.emailCorporativo || company?.email || "",
      whatsapp: company?.whatsapp || "",
      siteOficial: company?.siteOficial || "",
      enderecoCidade: company?.enderecoCidade || "",
      enderecoEstado: company?.enderecoEstado || "",
    });
    setIsEditModalOpen(true);
  };

  const handleCompanyFieldChange = (event) => {
    const { name, value } = event.target;
    setCompanyForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveCompany = async () => {
    if (!company?.id) return;

    setIsSavingCompany(true);
    setCompanyFormError("");

    try {
      const payload = {
        nomeFantasia: companyForm.nomeFantasia.trim() || null,
        razaoSocial: companyForm.razaoSocial.trim() || null,
        cnpj: companyForm.cnpj.trim() || null,
        statusVerificacao: companyForm.statusVerificacao,
        emailCorporativo: companyForm.emailCorporativo.trim() || null,
        whatsapp: companyForm.whatsapp.trim() || null,
        siteOficial: companyForm.siteOficial.trim() || null,
        enderecoCidade: companyForm.enderecoCidade.trim() || null,
        enderecoEstado: companyForm.enderecoEstado.trim() || null,
      };

      const response = await axiosInstance.put(`/companies/${company.id}`, payload);
      const updatedCompany = response?.data?.data || payload;

      setCompany((prev) => ({ ...prev, ...updatedCompany }));
      setIsEditModalOpen(false);
    } catch (error) {
      setCompanyFormError("Não foi possível salvar os dados do cliente. Tente novamente.");
    } finally {
      setIsSavingCompany(false);
    }
  };

  // Aprovação da verificação direto no topo — é a decisão principal da tela.
  const changeVerification = async (statusVerificacao) => {
    if (!company?.id) return;

    setVerificationSaving(true);
    setVerificationError("");

    try {
      const response = await axiosInstance.put(
        `/companies/${company.id}/verification`,
        { statusVerificacao }
      );
      const updated = response?.data?.data;
      setCompany((prev) => ({ ...prev, ...(updated || { statusVerificacao }) }));
    } catch (error) {
      setVerificationError(
        error?.response?.data?.message ||
          "Não foi possível atualizar a verificação da empresa."
      );
    } finally {
      setVerificationSaving(false);
    }
  };

  // O backend já devolve mediaUrls assinadas; media cru fica como fallback.
  const getProductImages = (product) => {
    const urls = Array.isArray(product?.mediaUrls) ? product.mediaUrls : [];
    const raw = Array.isArray(product?.media) ? product.media : [];
    const length = Math.max(urls.length, raw.length);

    return Array.from({ length }, (_, index) => {
      if (urls[index]) return urls[index];
      const item = raw[index];
      if (typeof item !== "string" || !item) return "";
      return item.startsWith("data:") ||
        item.startsWith("blob:") ||
        item.startsWith("http://") ||
        item.startsWith("https://")
        ? item
        : "";
    }).filter(Boolean);
  };

  const openProductPreview = (product) => {
    if (getProductImages(product).length === 0) return;
    setPreviewIndex(0);
    setPreviewProduct(product);
  };

  const applyProductStatus = async (product, statusAprovacao, motivoRejeicao = null) => {
    if (!product?.id) return false;

    setProductStatusSavingId(product.id);
    setProductStatusError("");

    try {
      const response = await axiosInstance.put(`/products/${product.id}/status`, {
        statusAprovacao,
        motivoRejeicao,
      });
      const updatedProduct = response?.data?.data;

      if (productsStatus) {
        // A linha sai da lista quando há filtro de status aplicado.
        setProductsRefreshKey((prev) => prev + 1);
      } else {
        setProducts((prev) =>
          prev.map((item) =>
            item.id === product.id ? { ...item, ...(updatedProduct || {}) } : item
          )
        );
      }

      return true;
    } catch (error) {
      setProductStatusError(
        error?.response?.data?.message ||
          "Não foi possível atualizar o status do produto. Tente novamente."
      );
      return false;
    } finally {
      setProductStatusSavingId(null);
    }
  };

  const openRejectProductModal = (product) => {
    setProductStatusError("");
    setRejectReason(product?.motivoRejeicao || "");
    setRejectingProduct(product);
  };

  const closeRejectProductModal = () => {
    if (productStatusSavingId) return;
    setRejectingProduct(null);
    setRejectReason("");
    setProductStatusError("");
  };

  const handleConfirmRejectProduct = async () => {
    if (!rejectingProduct) return;

    if (!rejectReason.trim()) {
      setProductStatusError("Informe o motivo da reprovação.");
      return;
    }

    const ok = await applyProductStatus(rejectingProduct, "REJEITADO", rejectReason.trim());
    if (ok) {
      setRejectingProduct(null);
      setRejectReason("");
    }
  };

  if (!canAccessCompanies) {
    return (
      <EmptyState
        icon={FiBriefcase}
        title="Acesso restrito"
        description="A visualização de clientes está disponível apenas para usuários POWER_USER ou ADMIN."
      />
    );
  }

  if (loading && !company) {
    return <div className="muted">Carregando cliente...</div>;
  }

  if (!company) {
    return (
      <EmptyState
        icon={FiBriefcase}
        title="Cliente não encontrado"
        description="Não foi possível encontrar os dados desse cliente."
      />
    );
  }

  const verificationStatus = company?.statusVerificacao || "PENDENTE";
  const city = [company?.enderecoCidade, company?.enderecoEstado].filter(Boolean).join("/");
  const address = [
    company?.enderecoRua,
    company?.enderecoNumero,
    company?.enderecoComplemento,
    company?.enderecoBairro,
  ]
    .filter(Boolean)
    .join(", ");
  const siteUrl = company?.siteOficial
    ? company.siteOficial.startsWith("http")
      ? company.siteOficial
      : `https://${company.siteOficial}`
    : "";
  const pendingDocuments = documents.filter((doc) => doc?.status === "PENDENTE").length;

  return (
    <div className="fade-in page-stack">
      <BreadcrumbTrail items={breadcrumbItems} />

      <div className="cdet-hero">
        <div className="cdet-hero__logo">
          {company?.logoUrl ? (
            <img src={company.logoUrl} alt={displayName} />
          ) : (
            initialsOf(displayName)
          )}
        </div>

        <div style={{ minWidth: 0 }}>
          <h2 className="cdet-hero__name">{displayName}</h2>
          {company?.razaoSocial && company.razaoSocial !== displayName && (
            <p className="cdet-hero__legal">{company.razaoSocial}</p>
          )}

          <div className="cdet-hero__badges">
            <StatusPill status={verificationStatus} size="lg" />
            {company?.tipoEmpresa && (
              <span className="plist-tag">
                {COMPANY_TYPE_LABEL[company.tipoEmpresa] || company.tipoEmpresa}
              </span>
            )}
            {company?.cnpj && <span className="plist-tag">CNPJ {company.cnpj}</span>}
            {verificationStatus === "APROVADO" && company?.aprovadoEm && (
              <span className="muted" style={{ fontSize: "0.78rem" }}>
                verificada {timeAgo(company.aprovadoEm)}
              </span>
            )}
          </div>

          <div className="cdet-hero__meta">
            {(company?.emailCorporativo || company?.email) && (
              <a href={`mailto:${company.emailCorporativo || company.email}`}>
                <FiMail size={14} />
                {company.emailCorporativo || company.email}
              </a>
            )}
            {company?.whatsapp && (
              <span>
                <FiPhone size={14} />
                {company.whatsapp}
              </span>
            )}
            {city && (
              <span>
                <FiMapPin size={14} />
                {city}
              </span>
            )}
            {siteUrl && (
              <a href={siteUrl} target="_blank" rel="noreferrer">
                <FiGlobe size={14} />
                {company.siteOficial}
                <FiExternalLink size={11} />
              </a>
            )}
          </div>
        </div>

        <div className="cdet-hero__actions">
          {verificationStatus !== "APROVADO" && (
            <button
              type="button"
              className="cdet-action cdet-action--approve"
              onClick={() => changeVerification("APROVADO")}
              disabled={verificationSaving}
            >
              <FiCheckCircle size={15} />
              {verificationSaving ? "Salvando..." : "Aprovar verificação"}
            </button>
          )}
          {verificationStatus !== "REJEITADO" && (
            <button
              type="button"
              className="cdet-action cdet-action--reject"
              onClick={() => changeVerification("REJEITADO")}
              disabled={verificationSaving}
            >
              <FiXCircle size={15} />
              Reprovar
            </button>
          )}
          {verificationStatus !== "PENDENTE" && (
            <button
              type="button"
              className="cdet-action"
              onClick={() => changeVerification("PENDENTE")}
              disabled={verificationSaving}
            >
              <FiClock size={15} />
              Voltar para análise
            </button>
          )}
          <button type="button" className="cdet-action" onClick={openEditModal}>
            <FiEdit2 size={15} />
            Editar dados
          </button>
        </div>
      </div>

      {verificationError && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            backgroundColor: "#fee2e2",
            color: "#b91c1c",
            padding: "0.75rem 0.9rem",
            borderRadius: "0.6rem",
            fontSize: "0.88rem",
          }}
        >
          <FiAlertTriangle size={16} />
          {verificationError}
        </div>
      )}

      <div className="cdet-stats">
        <div className="cdet-stat">
          <span className="cdet-stat__label">
            <FiPackage size={13} /> Produtos
          </span>
          <div className="cdet-stat__value">{tabCounts.products || 0}</div>
        </div>
        <div className="cdet-stat">
          <span className="cdet-stat__label">
            <FiSpeaker size={13} /> Anúncios
          </span>
          <div className="cdet-stat__value">{tabCounts.ads || 0}</div>
        </div>
        <div className={`cdet-stat${pendingDocuments > 0 ? " cdet-stat--alert" : ""}`}>
          <span className="cdet-stat__label">
            <FiFolder size={13} /> Documentos
          </span>
          <div className="cdet-stat__value">
            {tabCounts.documents || 0}
            {pendingDocuments > 0 && (
              <span style={{ fontSize: "0.8rem", marginLeft: 6 }}>
                · {pendingDocuments} pendente(s)
              </span>
            )}
          </div>
        </div>
        <div className="cdet-stat">
          <span className="cdet-stat__label">
            <FiTag size={13} /> Segmentação
          </span>
          <div className="cdet-stat__value">{tabCounts.segmentation || 0}</div>
        </div>
      </div>

      <div className="plist-toolbar">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`plist-tab${activeTab === tab.id ? " is-active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            <span className="plist-tab__count">{tabCounts[tab.id] || 0}</span>
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="section-card">
          <div className="cdet-section-head">
            <div>
              <h3>Dados do cliente</h3>
              <p>Cadastro enviado pelo anunciante e usado na vitrine.</p>
            </div>
          </div>

          <div className="cdet-grid">
            <div className="cdet-block">
              <h4 className="cdet-block__title">
                <FiBriefcase size={13} /> Identificação
              </h4>
              <Row label="Razão social">{company?.razaoSocial || company?.name}</Row>
              <Row label="Nome fantasia">{company?.nomeFantasia}</Row>
              <Row label="CNPJ">{company?.cnpj}</Row>
              <Row label="Inscrição estadual">{company?.inscricaoEstadual}</Row>
              <Row label="Tipo">
                {company?.tipoEmpresa
                  ? COMPANY_TYPE_LABEL[company.tipoEmpresa] || company.tipoEmpresa
                  : null}
              </Row>
            </div>

            <div className="cdet-block">
              <h4 className="cdet-block__title">
                <FiMail size={13} /> Contato
              </h4>
              <Row label="E-mail">{company?.emailCorporativo || company?.email}</Row>
              <Row label="E-mail público">{company?.isEmailPublic ? "Sim" : "Não"}</Row>
              <Row label="WhatsApp">{company?.whatsapp}</Row>
              <Row label="Site">{company?.siteOficial}</Row>
              <Row label="Instagram">{company?.instagramUrl}</Row>
              <Row label="LinkedIn">{company?.linkedinUrl}</Row>
            </div>

            <div className="cdet-block">
              <h4 className="cdet-block__title">
                <FiMapPin size={13} /> Endereço
              </h4>
              <Row label="Logradouro">{address}</Row>
              <Row label="Cidade/UF">{city}</Row>
              <Row label="CEP">{company?.enderecoCep}</Row>
            </div>

            <div className="cdet-block">
              <h4 className="cdet-block__title">
                <FiCheckCircle size={13} /> Verificação
              </h4>
              <Row label="Status">
                <StatusPill status={verificationStatus} />
              </Row>
              <Row label="Aprovada em">
                {company?.aprovadoEm ? formatDate(company.aprovadoEm) : null}
              </Row>
              <Row label="Limite de anúncios">{company?.limiteAnuncios}</Row>
              <Row label="Criada em">{formatDate(company?.createdAt)}</Row>
              <Row label="Atualizada em">{formatDate(company?.updatedAt)}</Row>
            </div>
          </div>

          {company?.bioEmpresa && (
            <div className="cdet-block" style={{ marginTop: 14 }}>
              <h4 className="cdet-block__title">
                <FiFileText size={13} /> Sobre a empresa
              </h4>
              <p style={{ margin: 0, fontSize: "0.9rem", lineHeight: 1.6 }}>
                {company.bioEmpresa}
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === "segmentation" && (
        <div className="section-card">
          <div className="cdet-section-head">
            <div>
              <h3>Segmentação</h3>
              <p>Categorias e regiões em que a empresa atua.</p>
            </div>
          </div>

          <div className="cdet-grid">
            <div className="cdet-block">
              <h4 className="cdet-block__title">
                <FiTag size={13} /> Categorias ({selectedCategoryLabels.length})
              </h4>
              {selectedCategoryLabels.length === 0 ? (
                <div className="muted" style={{ fontSize: "0.88rem" }}>
                  Nenhuma categoria vinculada.
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {selectedCategoryLabels.map((label) => (
                    <span key={label} className="plist-tag">
                      {label}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="cdet-block">
              <h4 className="cdet-block__title">
                <FiMapPin size={13} /> Regiões ({selectedRegionLabels.length})
              </h4>
              {selectedRegionLabels.length === 0 ? (
                <div className="muted" style={{ fontSize: "0.88rem" }}>
                  Nenhuma região vinculada.
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {selectedRegionLabels.map((label) => (
                    <span key={label} className="plist-tag">
                      {label}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "products" && (
        <div className="section-card">
          <div className="cdet-section-head">
            <div>
              <h3>Produtos</h3>
              <p>
                {productsMeta.totalCount} item(ns) no catálogo. Aprovar publica na
                vitrine imediatamente.
              </p>
            </div>
          </div>

          <div className="plist-filters">
            <div className="field" style={{ margin: 0 }}>
              <label className="label" htmlFor="cdet-products-search">
                Buscar
              </label>
              <div className="plist-search">
                <FiSearch className="plist-search__icon" size={16} />
                <input
                  id="cdet-products-search"
                  className="input"
                  type="text"
                  placeholder="Título do produto"
                  value={productsQuery}
                  onChange={(event) => {
                    setProductsQuery(event.target.value);
                    setProductsPage(1);
                  }}
                />
                {productsQuery && (
                  <button
                    type="button"
                    className="plist-search__clear"
                    onClick={() => {
                      setProductsQuery("");
                      setProductsPage(1);
                    }}
                    aria-label="Limpar busca"
                  >
                    <FiX size={13} />
                  </button>
                )}
              </div>
            </div>

            <div className="field" style={{ margin: 0 }}>
              <label className="label" htmlFor="cdet-products-status">
                Status
              </label>
              <select
                id="cdet-products-status"
                className="select"
                value={productsStatus}
                onChange={(event) => {
                  setProductsStatus(event.target.value);
                  setProductsPage(1);
                }}
              >
                <option value="">Todos</option>
                <option value="PENDENTE">Pendente</option>
                <option value="APROVADO">Aprovado</option>
                <option value="REJEITADO">Reprovado</option>
              </select>
            </div>

            <div className="field" style={{ margin: 0 }}>
              <label className="label" htmlFor="cdet-products-urgency">
                Urgência
              </label>
              <select
                id="cdet-products-urgency"
                className="select"
                value={productsUrgency}
                onChange={(event) => {
                  setProductsUrgency(event.target.value);
                  setProductsPage(1);
                }}
              >
                <option value="">Todas</option>
                <option value="urgent">Somente urgentes</option>
                <option value="normal">Somente sem urgência</option>
              </select>
            </div>

            <div className="field" style={{ margin: 0 }}>
              <label className="label" htmlFor="cdet-products-per-page">
                Por página
              </label>
              <select
                id="cdet-products-per-page"
                className="select"
                value={productsItemsPerPage}
                onChange={(event) => {
                  setProductsItemsPerPage(Number(event.target.value));
                  setProductsPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={-1}>Todos</option>
              </select>
            </div>
          </div>

          {productStatusError && !rejectingProduct && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                backgroundColor: "#fee2e2",
                color: "#b91c1c",
                padding: "0.75rem 0.9rem",
                borderRadius: "0.6rem",
                marginBottom: "0.9rem",
                fontSize: "0.88rem",
              }}
            >
              <FiAlertTriangle size={16} />
              {productStatusError}
            </div>
          )}

          {products.length === 0 ? (
            <NoData
              title="Nenhum produto encontrado"
              description="Este cliente ainda não tem produtos com esse recorte."
            />
          ) : (
            <>
              <div className="plist-scroll">
                <table className="plist-table" style={{ minWidth: 820 }}>
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th>Condição</th>
                      <th>Status</th>
                      <th>Atualizado</th>
                      <th style={{ textAlign: "right" }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => {
                      const status = product?.statusAprovacao || "PENDENTE";
                      const isSaving = productStatusSavingId === product.id;
                      const isMenuOpen = openProductMenuId === product.id;
                      const images = getProductImages(product);

                      return (
                        <tr key={product.id}>
                          <td>
                            <div className="plist-product">
                              {images.length > 0 ? (
                                <button
                                  type="button"
                                  className="plist-thumb"
                                  onClick={() => openProductPreview(product)}
                                  title="Ver fotos do produto"
                                >
                                  <img src={images[0]} alt={product?.title || "Produto"} />
                                  {images.length > 1 && (
                                    <span className="plist-thumb__count">
                                      +{images.length - 1}
                                    </span>
                                  )}
                                </button>
                              ) : (
                                <div
                                  className="plist-thumb plist-thumb--empty"
                                  title="Produto sem foto"
                                >
                                  <FiImage size={18} />
                                </div>
                              )}
                              <div>
                                <div className="plist-product__name">
                                  {product?.title || "Sem título"}
                                </div>
                                <div className="plist-product__meta">
                                  {product?.hasUrgency && (
                                    <span className="plist-tag plist-tag--urgent">
                                      <FiAlertTriangle size={11} />
                                      Laranja Alerta
                                    </span>
                                  )}
                                  {images.length === 0 && (
                                    <span style={{ color: "#b45309", fontWeight: 600 }}>
                                      sem foto
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td>{product?.priceCondition || "-"}</td>

                          <td>
                            <StatusPill status={status} />
                            {status === "REJEITADO" && product?.motivoRejeicao && (
                              <div className="plist-reason" title={product.motivoRejeicao}>
                                {product.motivoRejeicao}
                              </div>
                            )}
                          </td>

                          <td>
                            <div
                              className="plist-date"
                              title={formatDate(product?.updatedAt || product?.createdAt)}
                            >
                              {timeAgo(product?.updatedAt || product?.createdAt) || "-"}
                              <span>
                                {formatDate(product?.updatedAt || product?.createdAt)}
                              </span>
                            </div>
                          </td>

                          <td>
                            <div className="plist-actions">
                              <div
                                className="plist-review"
                                ref={isMenuOpen ? productMenuRef : null}
                              >
                                <button
                                  type="button"
                                  className="plist-review__trigger"
                                  disabled={isSaving}
                                  aria-haspopup="menu"
                                  aria-expanded={isMenuOpen}
                                  onClick={() =>
                                    setOpenProductMenuId((current) =>
                                      current === product.id ? null : product.id
                                    )
                                  }
                                >
                                  {isSaving ? "Salvando..." : "Revisar"}
                                  <FiChevronDown size={14} />
                                </button>

                                {isMenuOpen && (
                                  <div role="menu" className="plist-review__menu">
                                    {REVIEW_OPTIONS.filter(
                                      (option) => option.status !== status
                                    ).map((option) => {
                                      const OptionIcon = option.icon;

                                      return (
                                        <button
                                          key={option.status}
                                          type="button"
                                          role="menuitem"
                                          className="plist-review__item"
                                          style={{ color: option.color }}
                                          onClick={() => {
                                            setOpenProductMenuId(null);
                                            if (option.status === "REJEITADO") {
                                              openRejectProductModal(product);
                                            } else {
                                              applyProductStatus(product, option.status);
                                            }
                                          }}
                                        >
                                          <OptionIcon size={15} />
                                          {option.label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <PaginationControls
                currentPage={productsMeta.currentPage}
                totalPages={productsMeta.totalPages}
                onChangePage={setProductsPage}
              />
            </>
          )}
        </div>
      )}

      {activeTab === "ads" && (
        <div className="section-card">
          <div className="cdet-section-head">
            <div>
              <h3>Anúncios</h3>
              <p>{adsMeta.totalCount} anúncio(s) veiculado(s) por este cliente.</p>
            </div>
          </div>

          <div className="plist-filters">
            <div className="field" style={{ margin: 0 }}>
              <label className="label" htmlFor="cdet-ads-search">
                Buscar
              </label>
              <div className="plist-search">
                <FiSearch className="plist-search__icon" size={16} />
                <input
                  id="cdet-ads-search"
                  className="input"
                  type="text"
                  placeholder="Título do anúncio"
                  value={adsQuery}
                  onChange={(event) => {
                    setAdsQuery(event.target.value);
                    setAdsPage(1);
                  }}
                />
                {adsQuery && (
                  <button
                    type="button"
                    className="plist-search__clear"
                    onClick={() => {
                      setAdsQuery("");
                      setAdsPage(1);
                    }}
                    aria-label="Limpar busca"
                  >
                    <FiX size={13} />
                  </button>
                )}
              </div>
            </div>

            <div className="field" style={{ margin: 0 }}>
              <label className="label" htmlFor="cdet-ads-status">
                Status
              </label>
              <select
                id="cdet-ads-status"
                className="select"
                value={adsStatus}
                onChange={(event) => {
                  setAdsStatus(event.target.value);
                  setAdsPage(1);
                }}
              >
                <option value="">Todos</option>
                <option value="PENDENTE">Pendente</option>
                <option value="APROVADO">Aprovado</option>
                <option value="REJEITADO">Rejeitado</option>
                <option value="PAUSADO">Pausado</option>
                <option value="EXPIRADO">Expirado</option>
              </select>
            </div>

            <div className="field" style={{ margin: 0 }}>
              <label className="label" htmlFor="cdet-ads-audience">
                Público
              </label>
              <select
                id="cdet-ads-audience"
                className="select"
                value={adsAudience}
                onChange={(event) => {
                  setAdsAudience(event.target.value);
                  setAdsPage(1);
                }}
              >
                <option value="">Todos</option>
                <option value="ATACADO">Atacado</option>
                <option value="VAREJO">Varejo</option>
                <option value="FOOD_SERVICE">Food Service</option>
              </select>
            </div>

            <div className="field" style={{ margin: 0 }}>
              <label className="label" htmlFor="cdet-ads-per-page">
                Por página
              </label>
              <select
                id="cdet-ads-per-page"
                className="select"
                value={adsItemsPerPage}
                onChange={(event) => {
                  setAdsItemsPerPage(Number(event.target.value));
                  setAdsPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={-1}>Todos</option>
              </select>
            </div>
          </div>

          {ads.length === 0 ? (
            <NoData
              title="Nenhum anúncio encontrado"
              description="Este cliente ainda não tem anúncios com esse recorte."
            />
          ) : (
            <>
              <div className="plist-scroll">
                <table className="plist-table" style={{ minWidth: 760 }}>
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th>Público</th>
                      <th>Status</th>
                      <th>Visualizações</th>
                      <th>Cliques</th>
                      <th>Atualizado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ads.map((ad) => (
                      <tr key={ad.id}>
                        <td>
                          <span className="plist-seller__name">{ad?.title || "-"}</span>
                          {ad?.shortDescription && (
                            <div className="plist-product__meta">
                              <span>{ad.shortDescription}</span>
                            </div>
                          )}
                        </td>
                        <td>
                          <span className="plist-tag">{audienceLabel(ad?.targetAudience)}</span>
                        </td>
                        <td>
                          <span className="badge" data-tone={statusTone(ad?.status)}>
                            {statusLabel(ad?.status)}
                          </span>
                        </td>
                        <td>{ad?.viewsCount || 0}</td>
                        <td>{ad?.clicksCount || 0}</td>
                        <td>
                          <div className="plist-date" title={formatDate(ad?.updatedAt)}>
                            {timeAgo(ad?.updatedAt) || "-"}
                            <span>{formatDate(ad?.updatedAt)}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <PaginationControls
                currentPage={adsMeta.currentPage}
                totalPages={adsMeta.totalPages}
                onChangePage={setAdsPage}
              />
            </>
          )}
        </div>
      )}

      {activeTab === "documents" && (
        <div className="section-card">
          <div className="cdet-section-head">
            <div>
              <h3>Documentos</h3>
              <p>
                Comprovantes enviados para a verificação do CNPJ
                {pendingDocuments > 0 ? ` — ${pendingDocuments} aguardando análise.` : "."}
              </p>
            </div>
          </div>

          {documents.length === 0 ? (
            <NoData
              title="Nenhum documento enviado"
              description="Este cliente ainda não anexou documentos para verificação."
            />
          ) : (
            <div className="cdet-docs">
              {documents.map((doc) => (
                <div key={doc.id} className="cdet-doc">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 10,
                    }}
                  >
                    <span className="cdet-doc__icon">
                      <FiFileText size={18} />
                    </span>
                    <StatusPill status={doc?.status} />
                  </div>

                  <div>
                    <div className="cdet-doc__type">{doc?.type || "Documento"}</div>
                    <div className="cdet-doc__date">
                      Enviado em {formatDate(doc?.createdAt)}
                    </div>
                  </div>

                  {doc?.fileUrl ? (
                    <a
                      className="plist-link"
                      href={doc.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ alignSelf: "flex-start" }}
                    >
                      <FiExternalLink size={14} />
                      Ver arquivo
                    </a>
                  ) : (
                    <span className="muted" style={{ fontSize: "0.82rem" }}>
                      Arquivo não enviado
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ResponsiveModal
        isOpen={isEditModalOpen}
        onClose={() => {
          if (!isSavingCompany) setIsEditModalOpen(false);
        }}
        title="Editar dados do cliente"
        subtitle="empresa"
        size="lg"
        primaryAction={{
          label: isSavingCompany ? "Salvando..." : "Salvar alterações",
          onClick: handleSaveCompany,
          disabled: isSavingCompany,
        }}
        secondaryAction={{
          label: "Cancelar",
          onClick: () => setIsEditModalOpen(false),
          disabled: isSavingCompany,
        }}
      >
        {companyFormError && (
          <div
            style={{
              backgroundColor: "#fee2e2",
              color: "#b91c1c",
              padding: "0.75rem",
              borderRadius: "0.5rem",
              fontSize: "0.9rem",
              marginBottom: "0.9rem",
            }}
          >
            {companyFormError}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "0.9rem",
          }}
        >
          {[
            { name: "nomeFantasia", label: "Nome fantasia" },
            { name: "razaoSocial", label: "Razão social" },
            { name: "cnpj", label: "CNPJ" },
            { name: "emailCorporativo", label: "E-mail corporativo" },
            { name: "whatsapp", label: "WhatsApp" },
            { name: "siteOficial", label: "Site oficial" },
            { name: "enderecoCidade", label: "Cidade" },
            { name: "enderecoEstado", label: "Estado (UF)", maxLength: 2 },
          ].map((field) => (
            <div key={field.name}>
              <label className="label" htmlFor={field.name}>
                {field.label}
              </label>
              <input
                id={field.name}
                name={field.name}
                className="input"
                value={companyForm[field.name]}
                onChange={handleCompanyFieldChange}
                maxLength={field.maxLength}
              />
            </div>
          ))}

          <div>
            <label className="label" htmlFor="statusVerificacao">
              Status da verificação
            </label>
            <select
              id="statusVerificacao"
              name="statusVerificacao"
              className="select"
              value={companyForm.statusVerificacao}
              onChange={handleCompanyFieldChange}
            >
              <option value="PENDENTE">Pendente</option>
              <option value="APROVADO">Aprovado</option>
              <option value="REJEITADO">Rejeitado</option>
            </select>
          </div>
        </div>
      </ResponsiveModal>

      <ResponsiveModal
        isOpen={Boolean(rejectingProduct)}
        onClose={closeRejectProductModal}
        title="Reprovar produto"
        subtitle="catálogo"
        size="md"
        primaryAction={{
          label: productStatusSavingId ? "Reprovando..." : "Confirmar reprovação",
          onClick: handleConfirmRejectProduct,
          disabled: Boolean(productStatusSavingId),
        }}
        secondaryAction={{
          label: "Cancelar",
          onClick: closeRejectProductModal,
          disabled: Boolean(productStatusSavingId),
        }}
      >
        {productStatusError && (
          <div
            style={{
              backgroundColor: "#fee2e2",
              color: "#b91c1c",
              padding: "0.75rem",
              borderRadius: "0.5rem",
              marginBottom: "0.75rem",
              fontSize: "0.88rem",
            }}
          >
            {productStatusError}
          </div>
        )}
        <p style={{ marginTop: 0, color: "var(--ink-500)", fontSize: "0.92rem" }}>
          O produto <strong>{rejectingProduct?.title || "-"}</strong> deixará de aparecer no
          catálogo público. O motivo abaixo é exibido ao anunciante no painel dele.
        </p>
        <label className="label" htmlFor="motivoRejeicao">
          Motivo da reprovação
        </label>
        <textarea
          id="motivoRejeicao"
          className="textarea"
          rows={4}
          value={rejectReason}
          onChange={(event) => setRejectReason(event.target.value)}
          placeholder="Ex.: fotos com marca d'água de terceiros"
          style={{ width: "100%", resize: "vertical" }}
        />
      </ResponsiveModal>

      <ResponsiveModal
        isOpen={Boolean(previewProduct)}
        onClose={() => setPreviewProduct(null)}
        title={previewProduct?.title || "Fotos do produto"}
        subtitle="catálogo"
        size="lg"
        secondaryAction={{ label: "Fechar", onClick: () => setPreviewProduct(null) }}
      >
        {(() => {
          const images = getProductImages(previewProduct);
          if (images.length === 0) {
            return (
              <p className="muted" style={{ margin: 0 }}>
                Este produto não tem fotos enviadas.
              </p>
            );
          }

          const safeIndex = Math.min(previewIndex, images.length - 1);

          return (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              <div
                style={{
                  borderRadius: "0.75rem",
                  border: "1px solid var(--border)",
                  background: "#1a120b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <img
                  src={images[safeIndex]}
                  alt={`${previewProduct?.title || "Produto"} - foto ${safeIndex + 1}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "60vh",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </div>

              {images.length > 1 && (
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  {images.map((image, index) => (
                    <button
                      key={image}
                      type="button"
                      onClick={() => setPreviewIndex(index)}
                      style={{
                        padding: 0,
                        border:
                          index === safeIndex
                            ? "2px solid var(--brand-500)"
                            : "1px solid var(--border)",
                        borderRadius: "0.5rem",
                        overflow: "hidden",
                        background: "none",
                        cursor: "pointer",
                        lineHeight: 0,
                      }}
                    >
                      <img
                        src={image}
                        alt={`Miniatura ${index + 1}`}
                        style={{ width: 62, height: 62, objectFit: "cover", display: "block" }}
                      />
                    </button>
                  ))}
                </div>
              )}

              <a
                href={images[safeIndex]}
                target="_blank"
                rel="noreferrer"
                className="muted"
                style={{ fontSize: "0.82rem", textDecoration: "underline" }}
              >
                Abrir imagem em nova aba
              </a>
            </div>
          );
        })()}
      </ResponsiveModal>
    </div>
  );
};

export default CompanyDetailsPage;
